import animation from '@helpers/animation';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { Device, from } from '@helpers/media';
import styled, { css } from 'styled-components';
import { CarouselButtonProps } from './CarouselButton';

const Button = styled.button.attrs({ type: 'button' })<{
  appearance: 'default' | 'round';
  direction: 'left' | 'right';
  hideLine: boolean;
  hideLineOnMobile: boolean;
  hideWhenDisabled: boolean;
  theme: Pick<CarouselButtonProps, 'theme'>;
}>`
  width: 47px;
  height: 47px;
  --main-color: ${brand.black};
  --accent-color: ${brand.white};
  background-color: var(--main-color);
  outline: none;
  border: none;
  position: relative;
  transition: opacity 750ms 0ms ${animation.easeInOutCubic}, transform 350ms ${animation.easeInOutCubic};

  ${({ hideWhenDisabled }) =>
    hideWhenDisabled
      ? css`
          &:disabled {
            opacity: 0;
            pointer-events: none;
          }
        `
      : css`
          &:disabled {
            opacity: 0.5;
            pointer-events: none;
            cursor: not-allowed;
          }
        `}

  &[data-size='small'] {
    width: ${fluid(24, 42)};
    height: ${fluid(24, 42)};

    .chevron svg {
      height: ${fluid(12, 19)};
    }
  }

  &[data-size='normal'] {
    width: 47px;
    height: 47px;

    @media ${from(Device.Desktop)} {
      width: 60px;
      height: 60px;
    }
  }

  &:before {
    content: '';
    height: 200%;
    width: 1px;
    position: absolute;
    top: 50%;
    left: 52%;
    transform: translate(-50%, -50%);
    background-color: var(--main-color);
    z-index: 0;

    ${({ hideLine, hideLineOnMobile }) => {
      if (hideLine) {
        return css`
          display: none;
        `;
      }

      if (hideLineOnMobile) {
        return css`
          display: none;

          @media ${from(Device.Tablet)} {
            display: block;
          }
        `;
      }
    }}
  }

  .chevron {
    display: block;
    position: relative;
    width: 100%;
    height: 100%;
    transform: translateX(1px);

    svg {
      width: 11px;
      height: 19px;

      fill: var(--accent-color);
      color: var(--accent-color);
      z-index: 1;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);

      @media ${from(Device.Desktop)} {
        width: 14px;
        height: 24px;
      }
    }
  }

  ${({ direction }) =>
    direction === 'left'
      ? css`
          .chevron {
            transform: rotate(180deg) translateX(1px);
          }
        `
      : null}

  ${({ theme }) => {
    switch (theme) {
      case 'red':
        return css`
          --main-color: ${brand.red.base};
        `;

      case 'white':
        return css`
          --main-color: ${brand.white};
          --accent-color: ${brand.black};
        `;

      default:
        break;
    }
  }}

  .border-detail {
    display: none;
  }

  ${({ appearance }) =>
    appearance === 'round'
      ? css`
          border-radius: 50%;
          border: 1px solid;
          border-color: var(--main-color);
          background-color: transparent;

          .chevron svg {
            fill: var(--main-color);
            color: var(--main-color);
          }

          /** If appearance is round, hideLine by default */
          &::before {
            display: none;
          }

          @media (hover: hover) or (pointer: fine) {
            margin: 2px;

            .border-detail {
              display: block;
              position: absolute;
              inset: 0px;
              transition: transform 0.35s ${animation.easeInOutCubic};

              svg {
                color: var(--main-color);
                width: 100%;
                stroke-dasharray: 325;
                stroke-dashoffset: 325;
                transition: stroke-dashoffset 0.35s ${animation.easeInOutCubic};
              }
            }

            &:hover:not(:disabled) {
              transform: translateY(-2px);

              .border-detail {
                transform: rotate(90deg);

                svg {
                  stroke-dashoffset: 0;
                }
              }
            }
          }
        `
      : null}
`;

export const CarouselButtonStyles = {
  Button,
};
